import {Box, HBox} from '@buncha/components/core/view';
import {CustomText, H5Text} from '@buncha/components/core/text';
import React, {useCallback} from 'react';

import AppModal from '@buncha/common/components/CustomModal/AppModal';
import {CatalogItem} from '@buncha/batchDetails/types';
import {CustomImage} from '@buncha/components/core/Image';
import If from '@buncha/components/conditional/If';
import {Modules} from '@buncha/utils/types';
import {OrderItem} from '@buncha/itemDetails/types/ItemScanner';
import {ShopperScreens} from '@buncha/home/navigation/types';
import {Touchable} from '@buncha/components/core/touchable';
import {WrongItemScanAlertStyle} from './styles';
import font from '@buncha/appStyles/style/font';
import {useColorThemeType} from '@buncha/appStyles/hooks/theme';
import {useGetLocale} from '@buncha/localization/hooks';

interface Props {
  open: boolean;
  onClose: () => void;
  catalogItem: CatalogItem | null | undefined;
  itemToBePicked: OrderItem;
  unableToScanHandler: () => void;
}

const WronItemScanAlert: React.FC<Props> = props => {
  const {open, onClose, catalogItem, itemToBePicked, unableToScanHandler} =
    props;
  const theme = useColorThemeType();
  const {title, body, button} =
    useGetLocale(Modules.itemDetails, ShopperScreens.ItemDetails) ?? {};

  const getImageComponent = useCallback((url: string) => {
    return (
      <Box>
        <CustomImage style={WrongItemScanAlertStyle.image} source={url} />
      </Box>
    );
  }, []);

  const getItemCell = useCallback(
    (name: string, itemImageUrl: string, isItemToPick?: boolean) => {
      return (
        <Box style={WrongItemScanAlertStyle.itemContainer}>
          <If condition={isItemToPick}>
            <Box
              style={[
                WrongItemScanAlertStyle.pickItem,
                {
                  backgroundColor: theme?.background.yellow[100],
                },
              ]}>
              <CustomText
                style={{
                  color: theme?.text.blue[300],
                  fontFamily: font.fontTheme.muli[700].normal,
                }}>
                {title?.pickThisItem}
              </CustomText>
            </Box>
          </If>
          <HBox
            style={[
              WrongItemScanAlertStyle.itemDetailsContainer,
              {
                borderColor: theme?.text.gray[200],
              },
            ]}>
            {getImageComponent(itemImageUrl || '')}
            <H5Text
              style={[
                WrongItemScanAlertStyle.itemName,
                {
                  color: theme?.text.blue[300],
                  fontFamily: font.fonts.muliBold,
                },
              ]}>
              {name}
            </H5Text>
          </HBox>
        </Box>
      );
    },
    [
      getImageComponent,
      theme?.background.yellow,
      theme?.text.blue,
      theme?.text.gray,
      title?.pickThisItem,
    ],
  );

  const unableToScanOnPress = useCallback(() => {
    onClose();
    unableToScanHandler();
  }, [onClose, unableToScanHandler]);

  return (
    <AppModal
      isOpen={open}
      onClose={onClose}
      topCTAHandelr={onClose}
      title={title?.wrongItemScanned}
      hideBottomCta
      ctaTopTitle={button?.rescanBarcode}
      modalBody={
        <Box>
          <H5Text style={[WrongItemScanAlertStyle.alertMessage]}>
            {body?.wrongScan}
          </H5Text>
          {getItemCell(
            itemToBePicked?.name || '',
            itemToBePicked?.itemImageUrl || '',
            true,
          )}
          <Box style={WrongItemScanAlertStyle.doNotPickContainer}>
            <CustomText
              style={{
                color: theme?.text.red[100],
                fontFamily: font.fontTheme.muli[900].normal,
              }}>
              {body?.donotPick}
            </CustomText>
          </Box>
          {getItemCell(
            catalogItem?.name || '',
            catalogItem?.itemImageUrl || '',
          )}
        </Box>
      }
      bottomCTA={
        <Touchable onPress={unableToScanOnPress} activeOpacity={1}>
          <CustomText
            style={[
              WrongItemScanAlertStyle.textDecorationLine,
              {
                color: theme?.text.gray[100],
                fontFamily: font.fonts.muliBold,
              },
            ]}>
            {body?.uploadImage}
          </CustomText>
        </Touchable>
      }
    />
  );
};

export default React.memo(WronItemScanAlert);
